import type {
  SelectedPage as ContentSelectorSelectedPage,
  SelectedFile as ContentSelectorSelectedFile,
  SelectorType,
} from '@content-selector/stores/app'
import type { Selection } from '@lib/types/selections'
import type { CanvasSelection } from '@canvas-selector/appTypes'
import type { ComponentNode, EmbeddableProps } from '@canvas-builder/types/canvas'
import type { SfEvent } from '@lib/types/sfdc'
import type { Mutable } from '@lib/types/ts'
import type { CanvasRetrieve, File, User, Canvas, CanvasSection, FileContentTypeEnum } from '@openapi'
import type { OpenRequestPayload } from '@sdk/payload.types'
import type { Range } from '@lib/util/tsHelpers'
import type { Agenda, AgendaSelectorProps } from '@agenda-selector/types/agenda.type'
import type { CollectionSelectorProps } from '@collection-selector/types/collection-selector.type'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'
import type { EmbedLocation } from '@lib/types/app'

export const UI_MESSAGE = 'UI_MESSAGE' as const

export const UI_API_METHOD_TYPES = {
  UI_BROADCAST: 'ui_broadcast',
  UI_SELECT_CONTENT: 'ui_select_content',
  UI_SELECT_CANVASES: 'ui_select_canvases',
  UI_OPEN: 'ui_open',
  UI_EMBEDDABLE_READY: 'ui_embeddable_ready',
  UI_TOAST: 'ui_toast',
  UI_CAPTURE_APP_ERROR: 'ui_capture_app_error',
  UI_UPDATE_CANVAS: 'ui_update_canvas',
  UI_PRINT_CANVAS: 'ui_print_canvas',
  UI_OPEN_CANVAS_OVERLAY: 'ui_open_canvas_overlay',
  UI_OPEN_COLLECTION_PLAYER_OVERLAY: 'ui_open_collection_player_overlay',
  UI_SELECT_COLLECTION_PLAYER_CONTENT: 'ui_select_collection_player_content',
  UI_SELECT_AGENDA_CONTENT: 'ui_select_agenda_content',
  UI_UPDATE_LOCATION: 'ui_update_location',
  UI_PRESELECT_SFDC_MEETING_ID: 'ui_preselect_sfdc_meeting_id',
  UI_SET_POSTCALL_STYLE: 'ui_set_postcall_style',
  UI_ENABLE_POSTCALL_SUBMIT: 'ui_enable_postcall_submit',
  UI_DISABLE_POSTCALL_SUBMIT: 'ui_disable_postcall_submit',
  UI_COMPLETE_POSTCALL: 'ui_complete_postcall',
  UI_STOP_MEETING: 'ui_stop_meeting',
  UI_CANCEL_MEETING: 'ui_cancel_meeting',
  UI_RESUME_MEETING: 'ui_resume_meeting',
  UI_PROMPT_PIA: 'ui_prompt_pia',

  UI_CANVAS_NAVIGATE_NEXT_PAGE: 'ui_canvas_navigate_next_page',
  UI_CANVAS_NAVIGATE_PREVIOUS_PAGE: 'ui_canvas_navigate_previous_page',
  UI_CANVAS_NAVIGATE_PAGE: 'ui_canvas_navigate_page',

  UI_APP_LOADED: 'ui_app_loaded',
  UI_APP_RESIZE: 'ui_app_resize',

  UI_CLOSE_CANVAS_SECTION_EXECUTION: 'ui_close_canvas_section_execution',
  UI_CLOSE_CANVAS_DRAWER: 'ui_close_canvas_drawer',
  UI_OPEN_GLOBAL_POPUP: 'ui_open_global_popup',
  UI_CLOSE_GLOBAL_POPUP: 'ui_close_global_popup',
  UI_OPEN_SHARING_SETTINGS: 'ui_open_sharing_settings',
} as const

export const UI_MESSAGE_TYPES = {
  UI_MEETING_CANCELED: 'ui_meeting_canceled',
  UI_CANVAS_UPDATED: 'ui_canvas_updated',
  UI_SECTION_LIST_UPDATED: 'ui_section_list_updated',
  UI_APP_SET_DATA: 'ui_app_set_data',
  UI_APP_UPDATE_DATA: 'ui_app_update_data',
  UI_PROMPT_PIA_RESPONSE_STREAMED: 'ui_prompt_pia_response_streamed',
} as const

export const UI_NATIVE_MESSAGE_TYPES = [
  UI_MESSAGE_TYPES.UI_APP_SET_DATA,
  UI_MESSAGE_TYPES.UI_APP_UPDATE_DATA,
  UI_MESSAGE_TYPES.UI_CANVAS_UPDATED,
] as const

// UI API METHOD TYPES
export type UiBroadcastRequest = Record<string, any>

export type UiSelectContentRequest = {
  /** Array of selections */
  selections?: Selection[]
  content_type?: 'file' | 'slide' | 'content'
  allowed_selector_types?: SelectorType[]
  allowed_content_types?: FileContentTypeEnum[]
  allowed_asset_content_types?: FileContentTypeEnum[]
  max_selections?: number
  min_selections?: Range<0, 999>
  allowed_ids?: File['id'][]
  initial_metadata_filters?: Record<string, any>
  /**
   * Array of file extensions to scope out list of the results (optional). Only files with these extensions will be shown.
   */
  allowed_original_extensions?: string[]
  /**
   * Disables files from selection which are not sharable (optional).
   */
  disable_no_sharable?: boolean
  /**
   * Disables files from selection which do not have pptx_data in metadata object (optional).
   */
  disable_no_pptx_data?: boolean
  /**
   * Only allow content pdf pages (optional). User will be forced to select a page from the pdf.
   */
  only_allow_content_pdf_pages?: boolean
}

export type UiSelectContentResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected'
  /** Selected content */
  content?: (ContentSelectorSelectedFile | ContentSelectorSelectedPage)[]
}

export type UiSelectCollectionPlayerRequest = {
  initial_data?: CollectionSelectorProps['initialData']
  attach_to?: CollectionSelectorProps['attachTo']
  open_settings?: CollectionSelectorProps['openSettings']
  readonly_name?: CollectionSelectorProps['readonlyName']
  close_on_settings?: CollectionSelectorProps['closeOnSettings']
  translations?: CollectionSelectorProps['translations']
  initial_metadata_filters?: CollectionSelectorProps['initialMetadataFilters']
  hide_close?: CollectionSelectorProps['hideClose']
}

export type UiSelectCollectionPlayerResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected' | 'settings'
  /** Selected content */
  content?: CollectionPlayer
}

export type UiSelectAgendaRequest = {
  initial_agenda?: AgendaSelectorProps['initialAgenda']
  attach_to?: AgendaSelectorProps['attachTo']
}

export type UiSelectAgendaResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected'
  /** Selected content */
  content?: Agenda
}

export type UiSelectCanvasesRequest = {
  /** Array of selections */
  selections?: CanvasSelection[]
  allowed_types?: ('canvas' | 'canvas-template' | 'section' | 'section-template')[]
  allowed_ids?: Canvas['id'][]
  initial_metadata_filters?: Record<string, any>
}

export type UiSelectCanvasesResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected'
  /** Selected canvases */
  canvases?: Canvas[]
}

export type UiPromptPiaRequest = {
  /** Unique identifier of this prompt (needed for streaming event) */
  id: string
  /** Prompt to send to PIA */
  prompt: string
}

export type UiPromptPiaResponse = {
  /** Unique identifier of this prompt (needed for streaming event) */
  id: string
  /** PIA response */
  response: string
}

/**
 * Error object to send to Pitcher sentry instance
 */
export type UiCaptureAppErrorRequest = Record<string, any>

export type UiUpdateCanvasRequest = Mutable<CanvasRetrieve> & {
  /** Comma-separated list of field keys to return from the server */
  fields?: string
}

export type UiPrintCanvasRequest = {
  /** Canvas ID to print */
  id: CanvasRetrieve['id']
  /** Optional HTTPS URL for async download notifications */
  notify_url?: string
}

export type UiPrintCanvasResponse = {
  /** Job ID for tracking the async download */
  job_id?: string
  /** Indicates if the request was successful */
  success: boolean
}

export type UiOpenRequest = OpenRequestPayload & {
  /** Name of the app to open */
  app_name?: string
  /** Context to pass to the app */
  app_context?: Record<string, any>
  /** Embed location of the app */
  app_embed_location?: EmbedLocation
}

export type UiCompletePostcallRequest = {
  /** Indicates if the postcall was successfully submitted */
  was_successfully_submitted?: boolean
}

export type UiSetPostcallStyleRequest = Record<string, any>

export type UiAppResizeRequest = {
  /** Requested height as the number of pixels */
  height: number
}

export type UiCanvasNavigatePageRequest = {
  /** The id of the component we want to navigate to */
  page: ComponentNode['id']
}

export interface UiToastRequest {
  /**
   * Type of the toast, influences the default icon
   * @defaultValue **REQUIRED**
   */
  type: 'info' | 'error' | 'warning' | 'success' | 'loading'

  /**
   * Text to show inside the toast
   * @defaultValue **REQUIRED**
   */
  message: string

  /**
   * Optional, value in milliseconds
   * @defaultValue 3500
   */
  duration?: number

  /**
   * Visibility of the close button, default is true
   * @defaultValue true
   */
  closable?: boolean

  /**
   * Toast won't be hidden as long as it is hovered
   * @defaultValue true
   */
  keepAliveOnHover?: boolean

  /**
   * Set to `false` to hide the icon
   * @defaultValue true
   */
  showIcon?: boolean
}

export interface UiPreselectSfdcMeetingIdRequest {
  /** Salesforce Meeting ID */
  eventId: string
}

export interface UiPreselectSfdcMeetingIdResponse {
  /** Salesforce Event */
  event: SfEvent | null
}

export interface UiOpenCollectionPlayerOverlayGroup {
  /** File ID */
  id: string
  /** File name */
  name: string
  /** Optional: specific slides/pages to show from the file */
  slides?: Array<{
    /** The file ID (same as parent group.id) */
    file_id: string
    /** Zero-based page/slide index to show */
    slide_index: number
  }>
}

export interface UiOpenCollectionPlayerOverlayRequest {
  /** Array of files/slides to be played in the collection */
  groups: UiOpenCollectionPlayerOverlayGroup[]
  /** The display name for the collection */
  name: string
}

// UI API EVENT MESSAGE TYPES
export type UiMessagePayloads = {
  [UI_MESSAGE_TYPES.UI_MEETING_CANCELED]: UiMeetingCanceledPayload
  [UI_MESSAGE_TYPES.UI_APP_SET_DATA]: UiAppSetDataPayload
  [UI_MESSAGE_TYPES.UI_APP_UPDATE_DATA]: UiAppUpdateDataPayload
  [UI_MESSAGE_TYPES.UI_CANVAS_UPDATED]: UiCanvasUpdatedPayload
  [UI_MESSAGE_TYPES.UI_SECTION_LIST_UPDATED]: UiSectionListUpdatedPayload
  [UI_MESSAGE_TYPES.UI_PROMPT_PIA_RESPONSE_STREAMED]: UiPromptPiaResponseStreamedPayload
}

export type UiMessageEnvelope<
  T extends
    (typeof UI_MESSAGE_TYPES)[keyof typeof UI_MESSAGE_TYPES] = (typeof UI_MESSAGE_TYPES)[keyof typeof UI_MESSAGE_TYPES],
> = {
  /** Message type */
  type: T
  /** Message body */
  body: UiMessagePayloads[T]
}

export type UiMeetingCanceledPayload = {
  /** Account ID */
  accountId?: string
  /** Event ID */
  eventId?: string
}

export type UiAppSetDataPayload = {
  /** Client name */
  client: string | null
  /** Platform name */
  platform: string | undefined
  /** User information */
  user: User | null
  /** Edit mode status */
  is_edit_mode: boolean
  /** Call status */
  in_call: boolean
  /** Canvas data */
  canvas: CanvasRetrieve
  /** Section used in canvas */
  used_in_section: CanvasSection | null
  /** Component ID */
  component_id: string
  /** Embeddable settings */
  settings: EmbeddableProps['settings'] | null
  /** Width of the canvas */
  width: string | undefined
  /** Height of the canvas */
  height: string | undefined
  /** Full screen mode status */
  full_screen_mode: boolean | undefined
  /** Display mode */
  display_mode: string | undefined
  /** Section index in section list */
  section_index: number | null
  /** Section list ID */
  section_list_id: string | null
  /** Canvas correlation ID for tracking */
  canvas_correlation_id: string | undefined
}

export type UiAppUpdateDataPayload = {
  /** Call status */
  in_call: boolean
  /** Edit mode status */
  is_edit_mode: boolean
}

export type UiCanvasUpdatedPayload = {
  /** Canvas data */
  canvas: CanvasRetrieve
}

export type UiSectionListUpdatedPayload = {
  /** List of section IDs */
  section_list_ids: string[]
  /** Section list component ID */
  section_list_component_id: string
  /** Canvas data */
  canvas: CanvasRetrieve | null
}

export type UiPromptPiaResponseStreamedPayload = {
  /** Unique identifier of this prompt */
  id: string
  /** PIA response so far */
  partial_response: string
  /** Current chunk of the response */
  chunk: string
}

export interface UiOpenGlobalPopupRequest {
  /** Name of the app to open */
  app_name: string
  /** Extra context to pass to the app */
  extra_context?: Record<string, any>
}

export interface UiOpenGlobalPopupResponse {
  /** Whether the popup was successfully opened */
  success: boolean
}

export interface UiOpenSharingSettingsRequest {
  files: Array<{
    id: string
    name: string
    thumbnail_url?: string
    content_type?: string
  }>
  default_auth_strategy?: string
}

export interface UiOpenSharingSettingsResponse {
  user_action: 'confirmed' | 'cancelled'
  settings?: {
    auth_strategy:
      | 'none'
      | 'any_email'
      | 'specified_emails'
      | 'specified_domains'
      | 'verified_emails'
      | 'verified_domains'
    allowed_emails?: string[]
    allowed_domains?: string[]
    valid_until_days?: number | null
    allow_download?: boolean
  }
}
